/**
 * GuideWidgetRenderer — renders the current embedded feature widget
 * with ErrorBoundary fallback.
 *
 * Modal chrome is the WIDGET'S, never this renderer's. `use-guide-messages.ts`
 * resolves the mode with `widgetRegistry.getDisplayMode(feature, 'blended')`,
 * so a widget only reaches `displayMode: 'modal'` when it does NOT offer
 * 'blended' — i.e. when it is registered as a modal because it renders its own
 * `<Dialog>` (today: `pitch_recorder`, `motivational_form`; see
 * `widgets/registry.ts`, where the contract is stated on `displayModes`).
 *
 * This renderer used to wrap that case in a `<Dialog>` of its own. The result
 * was two stacked Radix modals for one widget: two overlays, two focus traps,
 * two scroll locks, and — because the widget portals its own content to
 * `body` — an EMPTY second modal box sitting behind it, titled with a
 * hard-coded, untranslated `feature.replace(/_/g, ' ')` ("Pitch Recorder").
 * Captured in the block-03 trace of 2026-09-19: `body[data-scroll-locked="3"]`
 * with a `dialog-content` whose only child was `<div class="px-6 pb-6">`.
 */

import { useTranslation } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import { Button } from '@tonnie/ui';
import { useCallback, type ErrorInfo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { widgetRegistry } from '../../widgets/registry';

interface GuideWidgetRendererProps {
  currentFeature: {
    chatId: string;
    feature: string;
    config: Record<string, unknown>;
    displayMode: 'blended' | 'modal';
  } | null;
  onFeatureComplete: (data?: unknown) => void;
  onFeatureCancel: () => void;
}

export function useRenderWidget({
  currentFeature,
  onFeatureComplete,
  onFeatureCancel,
}: GuideWidgetRendererProps) {
  const { t } = useTranslation();
  const { captureException } = useErrorCapture();

  const renderWidget = useCallback(() => {
    if (!currentFeature) return null;
    const entry = widgetRegistry.get(currentFeature.feature);
    if (!entry) {
      return (
        <div className="p-4 text-center text-muted-foreground text-sm" role="alert">
          {t('copilot.widgetNotFound', {
            name: currentFeature.feature,
          })}
        </div>
      );
    }

    const WidgetComp = entry.component;
    const widgetProps = {
      config: currentFeature.config,
      onComplete: (data?: unknown) => {
        onFeatureComplete(data);
      },
      onSelect: (value: unknown) => onFeatureComplete(value),
      onCancel: onFeatureCancel,
      displayMode: currentFeature.displayMode,
    };

    const errorFallback = (
      <div className="p-6 text-center" role="alert">
        <p className="text-sm text-destructive-accessible font-medium">
          {t('copilot.widgetError')}
        </p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onFeatureCancel}>
          {t('common.continue')}
        </Button>
      </div>
    );

    const onWidgetError = (err: unknown, info: ErrorInfo) => {
      captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: { component: 'CoPilotGuide', widget: currentFeature.feature },
        extra: { componentStack: info.componentStack },
      });
    };

    // Both display modes render the widget directly — see the docblock: a
    // `modal` widget brings its own chrome, so wrapping it here produced a
    // SECOND dialog.
    return (
      <ErrorBoundary fallback={errorFallback} onError={onWidgetError}>
        <WidgetComp {...widgetProps} />
      </ErrorBoundary>
    );
  }, [currentFeature, onFeatureComplete, onFeatureCancel, t, captureException]);

  return renderWidget;
}
